import { useNavigate, useParams } from 'react-router-dom';
import { ChatChannel, ChatChannelType } from '../models/ChatChannel';
import { useCallback } from 'react';
import { Channel } from 'stream-chat';

export function useRouter() {
    const navigate = useNavigate();
    const { tab } = useParams();

    const navigateFromChannelTypeAndId = useCallback((channelType: string, channelId: string, urlGoBack?: string, jumpToMessage?: string) => {
      const urlParams = []
      if(urlGoBack) {
        urlParams.push(`urlGoBack=${encodeURIComponent(urlGoBack)}`);
      }
      if(jumpToMessage) {
        urlParams.push(`jumpToMessage=${jumpToMessage}`);
      }
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;

      const suffixUrl = urlParams.length > 0 ? `?${urlParams.join('&')}` : '';
      
      if (channelType === ChatChannelType.Messaging) {
          navigate(`/home/${tabToNavigateTo}/chat/user/${channelId}${suffixUrl}`);
        }
        else if (channelType === ChatChannelType.Event) {
          navigate(`/home/${tabToNavigateTo}/chat/event/${channelId}${suffixUrl}`);
        }
        else if (channelType === ChatChannelType.Group) {
          navigate(`/home/${tabToNavigateTo}/chat/group/${channelId}${suffixUrl}`);
        }
    }, [navigate, tab]);

    const navigateFromChannel = useCallback((channel: Channel) => {
        navigateFromChannelTypeAndId(channel.type, channel?.id ?? '');
    }, [navigateFromChannelTypeAndId]);

    const navigateFromFullChannelId = useCallback((fullChannelId: string, urlGoBack?: string, jumpToMessage?: string) => {
        const [channelType, channelId] = fullChannelId.split(':');
        navigateFromChannelTypeAndId(channelType, channelId, urlGoBack, jumpToMessage);
    }, [navigateFromChannelTypeAndId]);

    const navigateToProfile = useCallback((channelType: string, id: string, urlGoBack?: string) => {
      let suffixUrl = urlGoBack ? `?urlGoBack=${encodeURIComponent(urlGoBack)}` : '';

      const tabToNavigateTo = tab === undefined ? 'chat' : tab;

      if (channelType === ChatChannelType.Messaging) {
        navigate(`/home/${tabToNavigateTo}/user/${id}${suffixUrl}`);
      }
      else if (channelType === ChatChannelType.Group) {
        navigate(`/home/${tabToNavigateTo}/group/${id}${suffixUrl}`);
      }
      else if (channelType === ChatChannelType.Event) {
        navigate(`/home/${tabToNavigateTo}/event-group/${id}${suffixUrl}`);
      }
    }, [navigate, tab]);

    const navigateToEvent = useCallback((id: string) => {
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;
      navigate(`/home/${tabToNavigateTo}/event/${id}`);
    }, [navigate, tab]);

    const navigateToEventParticipants = useCallback((id: string) => {
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;
      navigate(`/home/${tabToNavigateTo}/event/participants/${id}`);
    }, [navigate, tab]);

    const navigateToEventGroupChat = useCallback((id: string) => {
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;
      navigate(`/home/${tabToNavigateTo}/event-group/${id}`);
    }, [navigate, tab]);

    const navigateToGroup = useCallback((id: string) => {
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;
      navigate(`/home/${tabToNavigateTo}/group/${id}`);
    }, [navigate, tab]);

    const navigateToCall = useCallback(() => {
      const tabToNavigateTo = tab === undefined ? 'chat' : tab;
      navigate(`/home/${tabToNavigateTo}/call`);
    }, [navigate, tab]);

    const navigateToProfileFromChatChannel = useCallback((chatChannel: ChatChannel) => {
      if(chatChannel?.type && chatChannel?.id) {
        navigateToProfile(chatChannel.type, chatChannel.id);
      }
    }, [navigateToProfile]);

    const navigateToUserProfile = useCallback((id: string, urlGoBack?: string) => {
      navigateToProfile(ChatChannelType.Messaging, id, urlGoBack);
    }, [navigateToProfile]);

    return {
        navigateFromChannel,
        navigateFromFullChannelId,
        navigateToProfile,
        navigateToProfileFromChatChannel,
        navigateToUserProfile,
        navigateToEvent,
        navigateToGroup,
        navigateToEventGroupChat,
        navigateFromChannelTypeAndId,
        navigateToCall,
        navigateToEventParticipants
    }
}